import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getImageUrl } from '@/lib/imageUtils';

export default function PortalLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, perfil, signIn, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [brandLogo, setBrandLogo] = useState<string | null>(null);
  const [brandNombre, setBrandNombre] = useState<string>('Portal del cliente');

  // Branding opcional vía ?empresa=<uuid>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const empresaId = params.get('empresa');
    if (!empresaId) return;
    (async () => {
      const { data } = await supabase
        .from('empresas')
        .select('nombre, logo_url')
        .eq('id', empresaId)
        .maybeSingle();
      if (data) {
        setBrandNombre(data.nombre || 'Portal del cliente');
        setBrandLogo(getImageUrl(data.logo_url) || data.logo_url || null);
      }
    })();
  }, [location.search]);

  // Redirige si ya está logueado
  useEffect(() => {
    if (!isLoading && user) {
      if (perfil?.rol === 'cliente_portal') navigate('/portal', { replace: true });
      else navigate('/dashboard', { replace: true });
    }
  }, [user, perfil, isLoading, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await signIn(email, password);
    setSubmitting(false);
    if (!error) toast.success('Bienvenido');
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center space-y-3">
          {brandLogo ? (
            <img src={brandLogo} alt={brandNombre} className="h-16 mx-auto object-contain" />
          ) : (
            <div className="h-16 flex items-center justify-center">
              <LogIn className="h-12 w-12 text-primary" />
            </div>
          )}
          <CardTitle className="text-xl">{brandNombre}</CardTitle>
          <p className="text-sm text-muted-foreground">Acceso para clientes</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email}
                onChange={e => setEmail(e.target.value)} autoComplete="email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" required value={password}
                onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
              Entrar
            </Button>
            <p className="text-xs text-muted-foreground text-center pt-2">
              ¿Problemas para acceder? Contacta con tu proveedor de servicios.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
